import type { InvitationStage } from "../invitation-stage-filter.tsx";
import { useCallback, useEffect, useState } from "react";

import { GET_INVITATION_ONBOARDING_EMPLOYEE_LIST } from "../../../../../config/Endpoints.js";
import { getAuthToken } from "../../../../../utils/Storage.js";
import { ApiError, getApi } from "../../../../utils/api.ts";
import {
  parseOnboardingPagedBody,
  type OnboardInvitationRow,
} from "../schemas/onboard-invitation-row.schema.ts";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";
import { SQE_ONBOARDING, sqePageParams, sqeRows } from "../../onboarding/sqe/sqe-onboarding-api.ts";

function accessToken(): string | undefined {
  const t = getAuthToken("accessToken");
  return t != null && t !== "" ? t : undefined;
}

export function usePreJoinInvitations(
  selectedEmployeeId: string,
  page: number,
  pageSize: number,
  consultantType?: "REGULAR" | "INDIAN_CONSULTANT" | "DIRECT" | "VENDOR_BASED",
  enabled = true,
  stage: InvitationStage = "ALL",
) {
  const isSqe = useSqeLabs();
  const [rows, setRows] = useState<OnboardInvitationRow[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(
    async (signal?: AbortSignal) => {
      if (!enabled) return;
      setLoading(true);
      setError(null);
      const idParam =
        selectedEmployeeId.trim() !== ""
          ? `&id=${encodeURIComponent(selectedEmployeeId.trim())}`
          : "";
      const apiPage = Math.max(0, page - 1);
      const typeParam = consultantType ? `&consultantType=${consultantType}` : "";
      
      const stageParam = stage === "ALL" ? "" : `&stage=${stage}`;
      const sqeStatus = stage === "FORM_PENDING" ? "pending" : stage === "COMPLETED" ? "filled" : "all";
      const url = isSqe
        ? `${SQE_ONBOARDING}/invitations?${selectedEmployeeId ? `status=${sqeStatus}` : sqePageParams(page, pageSize, sqeStatus)}`
        : `${GET_INVITATION_ONBOARDING_EMPLOYEE_LIST}?status=all${stageParam}${idParam}&page=${apiPage}&pageSize=${pageSize}${typeParam}`;
      try {
        const { data } = await getApi<unknown>(url, accessToken(), "json", signal);
        if (signal?.aborted) return;
        const parsed = isSqe ? sqeRows(data) : parseOnboardingPagedBody(data);
        const normalizedRows = isSqe ? parsed.rows.map((row) => ({
          ...row,
          name: String(row.employeeName ?? row.name ?? ""),
          email: String(row.emailId ?? row.email ?? ""),
          createdOn: String(row.createdOn ?? row.createdAt ?? ""),
          onboardingStage: String(row.onboardingStage ?? row.status ?? ""),
        })).filter((row) => !selectedEmployeeId || String(row.tempUserId ?? row.id ?? "") === selectedEmployeeId) : parsed.rows;
        setRows(normalizedRows);
        setTotalPages(Math.max(1, parsed.totalPages));
        setTotalElements(isSqe && selectedEmployeeId ? normalizedRows.length : parsed.totalElements);
      } catch (e) {
        if (signal?.aborted) return;
        const msg = e instanceof ApiError ? e.message : "Failed to load invitations.";
        setError(msg);
        setRows([]);
        setTotalPages(1);
        setTotalElements(0);
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [selectedEmployeeId, page, pageSize, consultantType, enabled, stage, isSqe],
  );

  useEffect(() => {
    const ac = new AbortController();
    void refetch(ac.signal);
    return () => ac.abort();
  }, [refetch]);

  return { rows, totalPages, totalElements, loading, error, refetch };
}
